/**
 * @file index.tsx
 *
 * @fileoverview Renders the job search split view.
 */
import dynamic from 'next/dynamic';
import Router from 'next/router';
import React, { useContext, useEffect, useMemo } from 'react';
import Image from '~/jobiqo/components/Image';
import JobDetailPanel from '~/jobiqo/components/JobSearch/SplitView/JobDetailPanel';
import Layout from '~/jobiqo/components/Layout';
import { SearchMetatags } from '~/jobiqo/components/Search/search-metatags';
import { HeaderContext, HeaderContextInterface } from '~/jobiqo/context/header';
import { isApplicant } from '~/jobiqo/lib/auth';
import { Facet, FacetConfig } from '~/jobiqo/models/search';
import { User } from '~/jobiqo/models/user';
import {
  FaqItem,
  Job,
  Metatag,
  SearchApiJobResult,
} from '~/jobiqo/types/types';
import { FiltersSection } from '~/jobiqo/components/JobSearch/components/FiltersSection';
import OrganizationGalleryWidgetViewContent from '~/jobiqo/components/Organization/Widgets/Gallery/View/components/Content';
import OrganizationVideoGalleryWidgetViewCotent from '~/jobiqo/components/Organization/Widgets/VideoGallery/View/components/Content';
import OrganizationGalleryWidgetViewTitle from '~/jobiqo/components/Organization/Widgets/Gallery/View/components/Title';
import OrganizationVideoGalleryWidgetViewTitle from '~/jobiqo/components/Organization/Widgets/VideoGallery/View/components/Title';
import ToggleLoading from '~/jobiqo/components/JobAlert/ToggleLoading';
import { BackIcon } from '~/vcl/icons/Back';
import { HeroRow } from '~/vcl/organisms/04-landingpage/02-hero-row';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { VerticallyCentered } from '~/vcl/atoms/06-layout/01-alignment/03-vertical-align';
import { SplitViewLayout } from '~/vcl/templates/02-jobs/01-split-view';
import { AlignedCenter } from '~/vcl/atoms/06-layout/01-alignment/01-align-center';
import { Box } from '~/vcl/atoms/07-box';
import { SearchFaqSchema } from '~/jobiqo/components/JobSearch/SchemaOrg';
import { getTranslatedSegments } from '~/jobiqo/components/Search/utils/searchHelpers';
import { useURL } from '~/jobiqo/context/url';
import JobSearchMetatags from '../JobSearchMetatags';
import Pager from '~/jobiqo/components/Pager';
import AdUnitSearchTopSlot from '~/jobiqo/components/Slots/AdUnitSearchTopSlot';
import AdUnitSearchMiddleSlot from '~/jobiqo/components/Slots/AdUnitSearchMiddleSlot';
import AdUnitSearchBottomSlot from '~/jobiqo/components/Slots/AdUnitSearchBottomSlot';
import { KEY as IMAGE_GALLERY_KEY } from '~/jobiqo/components/Organization/Widgets/Gallery';
import { KEY as VIDEO_GALLERY_KET } from '~/jobiqo/components/Organization/Widgets/VideoGallery';
import BookmarkedJobsProvider from '~/jobiqo/components/BookmarkedJobs/context';
import SearchEmpty from '~/jobiqo/components/Search/search-empty';
import SEOSearchFooter from '~/jobiqo/components/Search/SeoSearchFooter';
import CurrentSearch from '~/jobiqo/components/Search/current-search';
import JobsSideList from '../List';
import JobSearchTitle from '../JobSearchTitle';
import { FormattedMessage } from 'react-intl';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { PAGE_TYPES, PageType } from '~/jobiqo/lib/analytics';
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
} from '~/vcl/atoms/25-accordion';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import styles from './splitViewView.module.scss';
import { WatchadoReals } from '~/jobiqo/components/Watchadoo';
import JobSearchBreadcrumbs, {
  hasJobSearchBreadcrumbItems,
} from '~/jobiqo/components/JobSearch/components/Breadcrumbs';

type JobSearchContainerSize = 'small' | 'medium' | 'large' | 'xLarge' | 'unset';

const JobAlertToggle = dynamic(import('~/jobiqo/components/JobAlert/Toggle'), {
  ssr: false,
  loading: () => <ToggleLoading />,
});

interface JobSearchViewProps {
  /**
   * The list of jobs.
   */
  jobs: SearchApiJobResult;
  /**
   * The radius search flag.
   */
  radiusSearch: boolean;
  /**
   * The current pathname;
   */
  pathname: string;
  /**
   * The current full path;
   */
  path: string;
  /**
   * The current querystring.
   */
  querystring: any;
  /**
   * Search page url.
   */
  jobsPage: string;
  /**
   * Number of items per page;
   */
  itemsPerPage: number;
  /**
   * Current job id in the url.
   */
  currentJobId?: string;
  /**
   * The current job.
   */
  currentJob: Job | null;
  /**
   * Is mobile flag.
   */
  isMobile: boolean;
  /**
   * The current user.
   */
  user?: User;
  /**
   * Function that gets called when a filter is toggled;
   */
  toggleFilter: (
    filter: string,
    selectedItems: string[],
    alias?: string,
  ) => void;
  /**
   * Function that gets the currently selected items.
   */
  getFilterSelectedItems: (querystring: any, facet: Facet) => string[];
  /**
   * The facet config for job search.
   */
  jobSearchFacetsConfig: FacetConfig[];
  /**
   * Function that gets the currently selected facets.
   */
  getFilterSelectedFacets: (querystring: any, facets: Facet[]) => Facet[];
  /**
   * If external anchors in the main job description should get rel=nofollow.
   */
  noFollowExternalLinks?: boolean;
  /**
   * If external anchors in the main job description should be rendered as plain text.
   */
  plainTextExternalLinks?: boolean;
  /**
   * Render only fulltext search controls.
   */
  fullTextOnly?: boolean;
  /**
   * Render with the default Layout wrapper.
   */
  renderLayout?: boolean;
  /**
   * Hide the site header when rendering with the default Layout wrapper.
   */
  hideHeader?: boolean;
  /**
   * Force context-based split-view routing outside the default jobs page.
   */
  shallowJobSearch?: boolean;
  /**
   * Wrap only the search form area in a box.
   */
  boxSearchForm?: boolean;
  /**
   * Container size for the search form and split view results.
   */
  containerSize?: JobSearchContainerSize;
  /**
   * Disable standalone cover-image overlap on job detail cards.
   */
  disableJobDetailCoverOverlap?: boolean;
  /**
   * Hide breadcrumbs above job detail cards.
   */
  hideJobDetailBreadcrumbs?: boolean;
  /**
   * Analytics page context for events fired from split-view detail interactions.
   */
  pageType?: PageType;
}

export const JobSearchView = ({
  jobs,
  radiusSearch,
  pathname,
  path,
  querystring,
  jobsPage,
  itemsPerPage,
  currentJob,
  currentJobId,
  user,
  isMobile,
  toggleFilter,
  getFilterSelectedItems,
  jobSearchFacetsConfig,
  getFilterSelectedFacets,
  noFollowExternalLinks = false,
  plainTextExternalLinks = false,
  fullTextOnly = false,
  renderLayout = true,
  hideHeader = false,
  shallowJobSearch = false,
  boxSearchForm = false,
  containerSize,
  disableJobDetailCoverOverlap = false,
  hideJobDetailBreadcrumbs = false,
  pageType = PAGE_TYPES.JOB_SEARCH,
}: JobSearchViewProps) => {
  const headerContext: HeaderContextInterface = useContext(HeaderContext);
  const { JOBS } = useFrontendConfig();
  const SEOHeaderImage = useMemo(() => jobs?.seoSearchContent?.headerImage, [
    jobs?.seoSearchContent?.headerImage,
  ]);

  const { setTranslatedUrlSegments } = useURL();
  useEffect(() => {
    let translatedUrlParts = getTranslatedSegments(path, jobs, jobsPage);
    setTranslatedUrlSegments(translatedUrlParts);
  }, [path, jobs, jobsPage]);

  useEffect(() => {
    if (currentJobId) {
      headerContext.setLeftButton &&
        headerContext.setLeftButton(
          <button onClick={(e) => Router.back()}>
            <BackIcon color="primary" height="30" width="30" title="Back" />
          </button>,
        );
    } else {
      headerContext.setLeftButton && headerContext.setLeftButton(null);
    }

    return () => {
      headerContext.setLeftButton && headerContext.setLeftButton(null);
    };
  }, [currentJobId]);

  const splitViewContent =
    jobs?.pages?.length === 0 ? (
      <VerticallyCentered>
        <SearchEmpty
          resetUrl={jobsPage}
          metadata={jobs.metatags as Metatag[]}
          querystring={querystring}
        />
      </VerticallyCentered>
    ) : (
      <BookmarkedJobsProvider
        initialState={jobs.pages?.filter((job) => job?.bookmarked) ?? []}
      >
        <SplitViewLayout
          inDetail={!!currentJobId}
          Title={
            !JOBS.JOB_SEARCH_TITLE_ABOVE_FILTERS ? (
              <JobSearchTitle jobs={jobs} />
            ) : (
              <Spacing />
            )
          }
          sidebar={
            jobs.pages ? (
              <>
                {jobs.result_count && (
                  /* Used for StatusCake monitoring, please no to change */
                  <span
                    data-test-id="jobs_result_count"
                    data-result-count={jobs.result_count}
                  ></span>
                )}
                {/*Only show job alert button/toggle for applicants and
                    anymous users*/}
                {!user || isApplicant(user) ? (
                  <Spacing marginLeft={6}>
                    <JobAlertToggle
                      metadata={jobs.metatags as Metatag[]}
                      jobCount={jobs.pages.length}
                      path={path}
                      querystring={querystring}
                      selectedFacets={getFilterSelectedFacets(
                        querystring,
                        jobs.facets as Facet[],
                      )}
                    />
                    <Spacing size="medium" />
                  </Spacing>
                ) : null}
                <CurrentSearch
                  facets={jobs?.facets as Facet[]}
                  toggleFilter={toggleFilter}
                  querystring={querystring}
                  pathname={pathname}
                  searchPath={jobsPage}
                />
                {jobs.pages && (
                  <JobsSideList
                    isMobile={isMobile}
                    pathname={pathname}
                    querystring={querystring}
                    path={path}
                    jobs={jobs.pages as Job[]}
                    shallowJobSearch={shallowJobSearch}
                  />
                )}
                {jobs.result_count && jobs.result_count > itemsPerPage && (
                  <AlignedCenter>
                    <Spacing />
                    <Pager
                      items={jobs.result_count}
                      itemsPerPage={itemsPerPage}
                      filtersToExclude={['jobId']}
                      querystring={querystring}
                    />
                  </AlignedCenter>
                )}
              </>
            ) : null
          }
        >
          <JobDetailPanel
            user={user}
            currentJob={currentJob}
            noFollowExternalLinks={noFollowExternalLinks}
            plainTextExternalLinks={plainTextExternalLinks}
            disableCoverOverlap={disableJobDetailCoverOverlap}
            hideBreadcrumbs={hideJobDetailBreadcrumbs}
            pageType={pageType}
            {...(jobs.result_count ? { totalCount: jobs.result_count } : {})}
          >
            <>
              <Spacing size="large" />
              {!!currentJob?.organizationProfile?.gallery?.length && (
                <>
                  <Text marginBottom={6} fontWeight={600}>
                    <OrganizationGalleryWidgetViewTitle />
                  </Text>
                  <OrganizationGalleryWidgetViewContent
                    organization={currentJob.organizationProfile}
                    boxId={IMAGE_GALLERY_KEY}
                  />
                  <Spacing size="large" />
                </>
              )}
              {!!currentJob?.organizationProfile?.videos?.length && (
                <>
                  <Text marginBottom={6} fontWeight={600}>
                    <OrganizationVideoGalleryWidgetViewTitle />
                  </Text>
                  <OrganizationVideoGalleryWidgetViewCotent
                    organization={currentJob.organizationProfile}
                    boxId={VIDEO_GALLERY_KET}
                  />
                  <Spacing size="large" />
                </>
              )}
            </>
          </JobDetailPanel>
        </SplitViewLayout>
      </BookmarkedJobsProvider>
    );

  const searchForm = (
    <FiltersSection
      pathname={pathname}
      querystring={querystring}
      facets={jobs?.facets as Facet[]}
      getFilterSelectedItems={getFilterSelectedItems}
      toggleFilter={toggleFilter}
      hiddenForm={!!currentJobId}
      jobSearchFacetsConfig={jobSearchFacetsConfig}
      searchUrl={jobsPage}
      fullTextOnly={fullTextOnly}
      radiusSearch={radiusSearch}
    />
  );

  const searchFormContent = boxSearchForm ? (
    <Box>{searchForm}</Box>
  ) : (
    searchForm
  );
  const searchFormContainerSize = containerSize ?? 'large';
  const splitViewContainerSize = containerSize ?? 'xLarge';
  const hasFooterBreadcrumbs = hasJobSearchBreadcrumbItems({
    jobsPage,
    facets: jobs?.facets as Facet[],
    querystring,
    currentJob,
  });
  const hasSeoFooterContent = !!jobs?.seoSearchContent?.footer;

  const content = (
    <>
      <>
        <AlignedCenter>
          <AdUnitSearchTopSlot />
        </AlignedCenter>
        <JobSearchMetatags
          jobs={jobs}
          {...(currentJob ? { currentJob } : null)}
          searchUrl={jobsPage}
        />
        {jobs.metatags && (
          <SearchMetatags metadata={jobs.metatags as Metatag[]} />
        )}
        {jobs.faq && <SearchFaqSchema faqItems={jobs.faq as FaqItem[]} />}
        {SEOHeaderImage ? (
          <HeroRow
            boxPosition="background"
            image={
              <Image
                src={SEOHeaderImage.url as string}
                alt={SEOHeaderImage.alt as string}
                {...(SEOHeaderImage.title && {
                  title: SEOHeaderImage.title,
                })}
                {...(SEOHeaderImage.caption && {
                  caption: SEOHeaderImage.caption,
                })}
                width={null}
                height={null}
                priority={true}
                convertFormat={false}
              />
            }
          >
            <Main size={searchFormContainerSize}>
              <Text
                textAlign="center"
                tag="h1"
                lineHeight={[6, 8]}
                fontSize={[6, 8]}
              >
                {jobs?.seoSearchContent?.title}
              </Text>
              <Spacing size="medium" />
              <Text textAlign="center" fontSize={4} fontWeight="bold">
                {jobs?.seoSearchContent?.subtitle}
              </Text>
              <Spacing size="large" />
              {searchFormContent}
            </Main>
          </HeroRow>
        ) : (
          <Main size={searchFormContainerSize}>
            {JOBS.JOB_SEARCH_TITLE_ABOVE_FILTERS && !SEOHeaderImage && (
              <Spacing size="large">
                <AlignedCenter>
                  <JobSearchTitle jobs={jobs} />
                </AlignedCenter>
              </Spacing>
            )}
            <Spacing size="large" />
            {searchFormContent}
            <AlignedCenter>
              <AdUnitSearchMiddleSlot />
              <WatchadoReals watchadoJobIds={(jobs as any)?.watchadoJobIds} />
            </AlignedCenter>
          </Main>
        )}
        <Spacing size="large" />
        <Main size={splitViewContainerSize}>
          {splitViewContent}
          <AlignedCenter>
            <AdUnitSearchBottomSlot />
          </AlignedCenter>
        </Main>
      </>
      {jobs.faq && (
        <Main size="xLarge">
          <Box
            className={styles.faqBox}
            padding={9}
            label={
              <Text tag="h2" marginBottom={0} paddingBottom={0} lineHeight={2}>
                <FormattedMessage
                  id="seo_search_content_faq_label"
                  defaultMessage="Frequently asked questions"
                />
              </Text>
            }
          >
            <Spacing size="large" />
            <Accordion collapsible={true} multipleItems={true}>
              {jobs.faq?.map((faq, index) => (
                <FlexItems key={index} gap={4} justifyContent="space-between">
                  <FlexItem flex={1}>
                    <AccordionItem value={index.toString()}>
                      <AccordionButton>
                        <Text tag="div" fontSize={3}>
                          {faq?.question}
                        </Text>
                      </AccordionButton>
                      {faq?.answer && (
                        <AccordionPanel>{faq.answer}</AccordionPanel>
                      )}
                    </AccordionItem>
                  </FlexItem>
                </FlexItems>
              ))}
            </Accordion>
          </Box>
        </Main>
      )}
      {hasFooterBreadcrumbs || hasSeoFooterContent ? (
        <Main size="xLarge">
          <Box padding={9}>
            <JobSearchBreadcrumbs
              jobsPage={jobsPage}
              facets={jobs?.facets as Facet[]}
              querystring={querystring}
              currentJob={currentJob}
              spacingAfter={hasSeoFooterContent}
            />
            {hasSeoFooterContent ? (
              <SEOSearchFooter
                id="footer"
                format={jobs?.seoSearchContent?.footerFormat}
                content={jobs?.seoSearchContent?.footer ?? ''}
              />
            ) : null}
          </Box>
        </Main>
      ) : null}
      <Spacing marginTop={[8, 12]} />
    </>
  );

  if (!renderLayout) {
    return content;
  }

  return (
    <Layout fullWidthContainer={true} hideHeader={hideHeader}>
      {content}
    </Layout>
  );
};
export default JobSearchView;
